import * as z from "zod";

const PLAN_CUSTOM_ANALYTICS_REPORT_TOOL_NAME = "plan-custom-analytics-report";
const PLAN_CUSTOM_ANALYTICS_REPORT_EDIT_TOOL_NAME =
  "plan-custom-analytics-report-edit";
const CREATE_CUSTOM_ANALYTICS_REPORT_TOOL_NAME =
  "create-custom-analytics-report";
const EDIT_CUSTOM_ANALYTICS_REPORT_TOOL_NAME = "edit-custom-analytics-report";

const CustomAnalyticsReportPlanSectionSchema = z.object({
  title: z.string().min(1),
  values: z.array(z.string().min(1)),
});

const CustomAnalyticsReportPlanSchema = z.object({
  title: z.string().min(1),
  description: z.string(),
  sections: z.array(CustomAnalyticsReportPlanSectionSchema),
});

const ReadyForConfirmationOutputSchema = z
  .object({
    status: z.literal("ready_for_confirmation"),
    reportPlan: z.string().optional(),
    reportPlanCard: CustomAnalyticsReportPlanSchema.optional(),
    customReportTitle: z.string().optional(),
  })
  .passthrough();

const CreatedReportOutputSchema = z
  .object({
    status: z.enum(["preparing", "ready"]),
    customReportUrl: z.string().min(1),
  })
  .passthrough();

export type CustomAnalyticsReportAction =
  | {
      type: "confirm";
      operation: "create" | "edit";
      label: string;
      message: string;
      plan: CustomAnalyticsReportPlan | null;
      reportPlan: string | null;
    }
  | { type: "view-report"; url: string };

export interface CustomAnalyticsReportPlan {
  title: string;
  description: string;
  sections: readonly CustomAnalyticsReportPlanSection[];
}

export interface CustomAnalyticsReportPlanSection {
  title: string;
  values: readonly string[];
}

export function tryParseCustomAnalyticsReportAction(
  toolName: string,
  output: unknown,
): CustomAnalyticsReportAction | null {
  if (toolName === "execute_code") {
    const result = z
      .object({ customAnalyticsReport: z.unknown() })
      .safeParse(output);
    return result.success
      ? tryParseCustomAnalyticsReportAction(
          CREATE_CUSTOM_ANALYTICS_REPORT_TOOL_NAME,
          result.data.customAnalyticsReport,
        )
      : null;
  }
  const readyForConfirmationOutput =
    ReadyForConfirmationOutputSchema.safeParse(output);
  if (
    (toolName === PLAN_CUSTOM_ANALYTICS_REPORT_TOOL_NAME ||
      toolName === CREATE_CUSTOM_ANALYTICS_REPORT_TOOL_NAME) &&
    readyForConfirmationOutput.success
  ) {
    const output = readyForConfirmationOutput.data;
    const reportPlan = output.reportPlan ?? null;
    return {
      type: "confirm",
      operation: "create",
      label: "Generate report",
      message: "Yes, create it.",
      plan: output.reportPlanCard ?? null,
      reportPlan,
    };
  }

  if (
    toolName === PLAN_CUSTOM_ANALYTICS_REPORT_EDIT_TOOL_NAME &&
    readyForConfirmationOutput.success
  ) {
    const output = readyForConfirmationOutput.data;
    const reportPlan = output.reportPlan ?? null;
    return {
      type: "confirm",
      operation: "edit",
      label: "Update report",
      message: "Yes, update it.",
      plan: output.reportPlanCard ?? null,
      reportPlan,
    };
  }

  if (
    toolName === CREATE_CUSTOM_ANALYTICS_REPORT_TOOL_NAME ||
    toolName === EDIT_CUSTOM_ANALYTICS_REPORT_TOOL_NAME
  ) {
    const result = CreatedReportOutputSchema.safeParse(output);
    if (result.success) {
      return { type: "view-report", url: result.data.customReportUrl };
    }
  }

  return null;
}

export function confirmationPromptForCustomAnalyticsReportAction(
  action: Extract<CustomAnalyticsReportAction, { type: "confirm" }>,
): string {
  return action.operation === "edit"
    ? "Does this look right, and should I update it?"
    : "Does this look right, and should I create it?";
}
